import { uniq } from 'lodash'
import { Page } from '../../../models/util.model'
import { ProductListingKeys } from '../models/products-listing.model'
import { Product, ProductPageData } from '../product.model'

/**
 * Parse product page keys
 * @param page - the products page
 * @returns the page listing keys
 */
export function parseProductPageKeys(page: Page<Product>): ProductListingKeys {
  return page.data.reduce<ProductListingKeys>(
    (acc, r) => parseProductKeys(r, acc),
    {},
  )
}

/**
 * Parse product keys
 * @param product - the product
 * @param keys - the keys already loaded
 * @returns the product listing keys
 */
export function parseProductKeys(
  product: ProductPageData,
  keys: ProductListingKeys = {},
): ProductListingKeys {
  const attributesCodes = uniq([
    ...(keys['attributesCodes'] || []),
    ...(product.attributes?.map((a) => a.attributeCode) ?? []),
  ])

  const brandsIds = uniq([
    ...(keys['brandsIds'] || []),
    ...(product.brandId ? [product.brandId] : []),
  ])
  const categoriesIds = uniq([
    ...(keys['categoriesIds'] || []),
    ...(product.categories?.flatMap((c) => {
      const ids = (c.ids ?? []).filter((id) => !!id?.length)
      if (c.primaryId && !ids.includes(c.primaryId)) {
        return [...ids, c.primaryId]
      }
      return ids
    }) ?? []),
  ])
  const channelsIds = uniq([
    ...(keys['externalSKUs.channelId'] || []),
    ...(product.externalSKUs
      ?.map((s) => s.channelId)
      .filter((id): id is string => !!id?.length) ?? []),
    ...(product.channels
      ?.map((s) => s._id)
      .filter((id): id is string => !!id?.length) ?? []),
  ])
  const familiesIds = uniq([
    ...(keys['familiesIds'] || []),
    ...(product.family?.code ? [product.family?.code] : []),
  ])
  const kitsIds = uniq([
    ...(keys['kitsIds'] || []),
    ...(product.kitIds
      ? Array.isArray(product.kitIds)
        ? product.kitIds
        : [product.kitIds]
      : []),
  ])
  const locationsIds = uniq([
    ...(keys['locationsIds'] || []),
    ...(product.sites?.filter((s) => s.locationId !== undefined)
      ? [...product.sites.map((s) => s.locationId)]
      : []),
  ])
  const manufacturersIds = uniq([
    ...(keys['manufacturersIds'] || []),
    ...(product.manufacturerId ? [product.manufacturerId] : []),
  ])
  const parentsIds = uniq([
    ...(keys['parentsIds'] || []),
    ...(product.parentId ? [product.parentId] : []),
    ...(product.parent?._id ? [product.parent?._id] : []),
  ])
  const tagsIds = uniq([
    ...(keys['tagsIds'] || []),
    ...(product.tags?.filter((s) => s !== undefined)
      ? [...product.tags.map((s) => s)]
      : []),
  ])
  const suppliersIds = uniq([
    ...(keys['suppliersIds'] || []),
    ...(product.suppliers?.filter((s) => s.supplierId !== undefined)
      ? [...product.suppliers.map((s) => s.supplierId ?? '')]
      : []),
  ])

  return {
    attributesCodes,
    brandsIds,
    categoriesIds,
    'externalSKUs.channelId': channelsIds,
    familiesIds,
    kitsIds,
    locationsIds,
    manufacturersIds,
    parentsIds,
    tagsIds,
    suppliersIds,
  }
}
